import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UiState {
  theme: 'light' | 'dark' | 'system';
  sidebarOpen: boolean;
  sidebarWidth: number;
  commandPaletteOpen: boolean;
  settingsOpen: boolean;
  searchOpen: boolean;
  createDialogOpen: boolean;
  notifications: Notification[];
  // Layout state
  layout: {
    projectSelectorExpanded: boolean;
    filePanelWidth: number;
    editorPanelWidth: number;
  };
  // File list filters
  fileFilters: {
    searchQuery: string;
    selectedType: 'all' | 'document' | 'phrase';
    selectedTags: string[];
    sortBy: 'name' | 'modified' | 'created' | 'type';
    sortOrder: 'asc' | 'desc';
  };
  // Recent phrases tracking
  recentPhrases: {
    id: string;
    path: string;
    title: string;
    lastUsed: string;
  }[];
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  timestamp: number;
}

const initialState: UiState = {
  theme: 'system',
  sidebarOpen: true,
  sidebarWidth: 280,
  commandPaletteOpen: false,
  settingsOpen: false,
  searchOpen: false,
  createDialogOpen: false,
  notifications: [],
  layout: {
    projectSelectorExpanded: true,
    filePanelWidth: 320,
    editorPanelWidth: 0, // Will be calculated dynamically
  },
  fileFilters: {
    searchQuery: '',
    selectedType: 'all',
    selectedTags: [],
    sortBy: 'modified',
    sortOrder: 'desc',
  },
  recentPhrases: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarWidth: (state, action: PayloadAction<number>) => {
      state.sidebarWidth = action.payload;
    },
    toggleCommandPalette: (state) => {
      state.commandPaletteOpen = !state.commandPaletteOpen;
    },
    toggleSettings: (state) => {
      state.settingsOpen = !state.settingsOpen;
    },
    toggleSearch: (state) => {
      state.searchOpen = !state.searchOpen;
    },
    toggleCreateDialog: (state) => {
      state.createDialogOpen = !state.createDialogOpen;
    },
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>
    ) => {
      state.notifications.push({
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    // Layout actions
    toggleProjectSelector: (state) => {
      state.layout.projectSelectorExpanded =
        !state.layout.projectSelectorExpanded;
    },
    setFilePanelWidth: (state, action: PayloadAction<number>) => {
      state.layout.filePanelWidth = action.payload;
    },
    setEditorPanelWidth: (state, action: PayloadAction<number>) => {
      state.layout.editorPanelWidth = action.payload;
    },
    // File filter actions
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.fileFilters.searchQuery = action.payload;
    },
    setFileType: (
      state,
      action: PayloadAction<'all' | 'document' | 'phrase'>
    ) => {
      state.fileFilters.selectedType = action.payload;
    },
    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.fileFilters.selectedTags = action.payload;
    },
    addSelectedTag: (state, action: PayloadAction<string>) => {
      if (!state.fileFilters.selectedTags.includes(action.payload)) {
        state.fileFilters.selectedTags.push(action.payload);
      }
    },
    removeSelectedTag: (state, action: PayloadAction<string>) => {
      state.fileFilters.selectedTags = state.fileFilters.selectedTags.filter(
        (tag) => tag !== action.payload
      );
    },
    setSortBy: (
      state,
      action: PayloadAction<'name' | 'modified' | 'created' | 'type'>
    ) => {
      state.fileFilters.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.fileFilters.sortOrder = action.payload;
    },
    // Recent phrases actions
    addRecentPhrase: (
      state,
      action: PayloadAction<{ id: string; path: string; title: string }>
    ) => {
      const existing = state.recentPhrases.findIndex(
        (p) => p.id === action.payload.id
      );
      const newPhrase = {
        ...action.payload,
        lastUsed: new Date().toISOString(),
      };
      if (existing !== -1) {
        state.recentPhrases.splice(existing, 1);
      }
      state.recentPhrases.unshift(newPhrase);
      state.recentPhrases = state.recentPhrases.slice(0, 10); // Keep only 10 recent
    },
    removeRecentPhrase: (state, action: PayloadAction<string>) => {
      state.recentPhrases = state.recentPhrases.filter(
        (p) => p.id !== action.payload
      );
    },
    clearRecentPhrases: (state) => {
      state.recentPhrases = [];
    },
  },
});

export const {
  setTheme,
  toggleSidebar,
  setSidebarWidth,
  toggleCommandPalette,
  toggleSettings,
  toggleSearch,
  toggleCreateDialog,
  addNotification,
  removeNotification,
  clearNotifications,
  // Layout actions
  toggleProjectSelector,
  setFilePanelWidth,
  setEditorPanelWidth,
  // File filter actions
  setSearchQuery,
  setFileType,
  setSelectedTags,
  addSelectedTag,
  removeSelectedTag,
  setSortBy,
  setSortOrder,
  // Recent phrases actions
  addRecentPhrase,
  removeRecentPhrase,
  clearRecentPhrases,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
